// api.js — Backend communication

const BASE = import.meta.env?.VITE_API_URL ?? '/api';
// Dev-phase shared token (build-time). When set, sent as Bearer auth so the
// dev-gated api function accepts the request. Empty in production builds.
const DEV_TOKEN = import.meta.env?.VITE_DEV_AUTH_TOKEN ?? '';

function authHeaders() {
  const h = { 'Content-Type': 'application/json' };
  if (DEV_TOKEN) h['Authorization'] = `Bearer ${DEV_TOKEN}`;
  return h;
}

// The session cookie lives on .solounavolta.it, but the site and the API are
// different origins — without this fetch drops it and the server sees an
// anonymous caller, silently and with no error to notice.
const WITH_SESSION = { credentials: 'include' };

export async function getMe() {
  try {
    const res = await fetch(`${BASE}/me`, { headers: authHeaders(), ...WITH_SESSION });
    return res.ok ? res.json() : null;   // { email, plan, daily_limit }
  } catch {
    // Being unable to tell is the same as being logged out: the page still
    // works, just without the extras.
    return null;
  }
}

export async function requestMagicLink(email) {
  // Always resolves the same way. The server answers 204 whether or not the
  // address exists, and the UI must not undo that by reacting differently.
  try {
    await fetch(`${BASE}/auth/magic`, {
      method: 'POST', headers: authHeaders(), ...WITH_SESSION,
      body: JSON.stringify({ email }),
    });
  } catch { /* same silence */ }
}

export async function registerAccount(email, trap) {
  // Same silence as requestMagicLink, and for the same reason: a register form
  // that reacted differently for an address that already has an account would
  // rebuild the enumeration oracle on the other side of the split.
  //
  // The challenge is fetched here rather than with the page so it cannot go
  // stale while the form sits open.
  //
  // `trap` is read from the hidden field by the caller, never hardcoded here:
  // sending a constant empty string would make the honeypot pure theatre, since
  // the only thing it can ever catch is something that filled the DOM in.
  try {
    const res = await fetch(`${BASE}/auth/challenge`, {
      headers: authHeaders(), ...WITH_SESSION,
    });
    const { challenge } = await res.json();
    await fetch(`${BASE}/auth/register`, {
      method: 'POST', headers: authHeaders(), ...WITH_SESSION,
      body: JSON.stringify({ email, challenge, trap }),
    });
  } catch { /* same silence */ }
}

export async function requestUpgrade() {
  // Reports the outcome, unlike requestMagicLink: the caller is authenticated
  // and asking about their own account, so there is nothing to stay silent about.
  const res = await fetch(`${BASE}/upgrade`, {
    method: 'POST', headers: authHeaders(), ...WITH_SESSION,
  });
  // 'already' is not a failure: the request is on its way, just not twice.
  if (res.status === 429) return 'already';
  return res.status === 204;
}

export async function deleteAccount() {
  const res = await fetch(`${BASE}/me`, {
    method: 'DELETE', headers: authHeaders(), ...WITH_SESSION,
  });
  return res.status === 204;
}

export async function logout() {
  try {
    await fetch(`${BASE}/auth/logout`, { method: 'POST', headers: authHeaders(), ...WITH_SESSION });
  } catch { /* the cookie expires on its own */ }
}

// `copies` asks for one independent single-use link per recipient. The server
// clamps it to the plan, so a rejection here would be redundant.
export async function createMessage(ciphertext, ttlSeconds, copies = 1) {
  const res = await fetch(`${BASE}/message`, {
    method: 'POST',
    headers: authHeaders(),
    ...WITH_SESSION,
    body: JSON.stringify({ ciphertext, ttl: ttlSeconds, copies }),
  });
  if (!res.ok) {
    // Messages the user can act on, instead of a bare status code. The text
    // must reassure that what they wrote is not lost — it is still in the
    // textarea, and that is the first thing they will worry about.
    const messages = {
      400: 'Il messaggio è troppo lungo. Prova ad accorciarlo.',
      429: 'Hai raggiunto il limite di messaggi gratuiti per oggi. Riprova domani, oppure passa a un piano a pagamento per inviarne quanti ne vuoi.',
      503: 'Il servizio è temporaneamente in manutenzione. Il tuo messaggio non è stato inviato: riprova tra qualche minuto.',
    };
    const err = new Error(messages[res.status]
      ?? 'Non siamo riusciti a contattare il server. Il tuo messaggio non è stato inviato — riprova.');
    err.status = res.status;
    throw err;
  }
  return res.json(); // { token, delete_token, links: [...] }
}

// Fetches the ciphertext WITHOUT destroying it. The message is only destroyed
// once burnMessage() confirms the decryption succeeded — otherwise a truncated
// key or a dropped response would destroy content nobody ever read.
export async function getMessage(token) {
  const res = await fetch(`${BASE}/message/${token}`, { headers: authHeaders(), ...WITH_SESSION });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  return res.json(); // { ciphertext }
}

export async function burnMessage(token) {
  try {
    await fetch(`${BASE}/message/${token}/burn`, {
      method: 'POST',
      headers: authHeaders(),
      // Survives the tab being closed right after reading: the browser sends
      // it anyway instead of cancelling the request.
      keepalive: true,
    });
  } catch {
    // The reader already has the plaintext on screen; failing to confirm must
    // not turn into an error for them. The message expires on its own anyway.
  }
}

export async function deleteMessage(token, deleteToken) {
  const res = await fetch(`${BASE}/message/${token}?dt=${encodeURIComponent(deleteToken)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (res.status === 204) return { ok: true };
  if (res.status === 404) return { ok: false, error: 'not_found' };
  if (res.status === 403) return { ok: false, error: 'forbidden' };
  throw new Error(`Revoke failed: ${res.status}`);
}
